export enum PrescriptionLockedStatus {
    Draft = 0,
    Locked = 1,
}

export enum PrescriptionUnlockReason {
    NewClinicalInformation = 1,
    TranscriptionError = 2,
    HospitalReconciliation = 3,
    SpecialistChange = 4,
    PatientReported = 5,
    Other = 99,
}

export const PRESCRIPTION_UNLOCK_REASONS: PrescriptionUnlockReason[] = [
    PrescriptionUnlockReason.NewClinicalInformation,
    PrescriptionUnlockReason.TranscriptionError,
    PrescriptionUnlockReason.HospitalReconciliation,
    PrescriptionUnlockReason.SpecialistChange,
    PrescriptionUnlockReason.PatientReported,
    PrescriptionUnlockReason.Other,
];

export interface IPrescription {
    id: number;
    drugName: string;
    doseFrequencyID: number;
    unitID: number;
    dose: number;
    startDate: Date | undefined;
    endDate: Date | undefined;
    lockedStatus: PrescriptionLockedStatus;
    validatedByHcpID?: number | null;
    validatedAtUtc?: Date | null;
    validatedByName?: string | null;
}

export class Prescription implements IPrescription {
    id: number;
    drugName: string;
    doseFrequencyID: number;
    unitID: number;
    dose: number;
    startDate: Date | undefined;
    endDate: Date | undefined;
    lockedStatus: PrescriptionLockedStatus;
    validatedByHcpID?: number | null;
    validatedAtUtc?: Date | null;
    validatedByName?: string | null;

    constructor(data: IPrescription) {
        this.id = data.id;
        this.drugName = data.drugName;
        this.doseFrequencyID = data.doseFrequencyID;
        this.unitID = data.unitID;
        this.dose = data.dose;
        this.startDate = data.startDate ? new Date(data.startDate) : undefined;
        this.endDate = data.endDate ? new Date(data.endDate) : undefined;
        this.lockedStatus = data.lockedStatus ?? PrescriptionLockedStatus.Draft;
        this.validatedByHcpID = data.validatedByHcpID ?? null;
        this.validatedAtUtc = data.validatedAtUtc ? new Date(data.validatedAtUtc) : null;
        this.validatedByName = data.validatedByName ?? null;
    }

    get isLocked(): boolean {
        return this.lockedStatus === PrescriptionLockedStatus.Locked;
    }
}

export class PrescriptionData {
    units: { id: number; name: string }[];
    doseFrequencies: { id: number; name: string }[];
    prescriptions: Prescription[];

    constructor(data: {
        units: { id: number; name: string }[];
        doseFrequencies: { id: number; name: string }[];
        prescriptions: IPrescription[];
    }) {
        this.units = data.units;
        this.doseFrequencies = data.doseFrequencies;
        this.prescriptions = data.prescriptions.map((p) => new Prescription(p));
    }

    get lockedCount(): number {
        return this.prescriptions.filter((p) => p.isLocked).length;
    }

    get totalCount(): number {
        return this.prescriptions.length;
    }

    get hasDrafts(): boolean {
        return this.prescriptions.some((p) => !p.isLocked);
    }

    get mostRecentValidatedAt(): Date | null {
        const locked = this.prescriptions.filter((p) => p.isLocked && p.validatedAtUtc);
        if (locked.length === 0) return null;
        return locked
            .map((p) => p.validatedAtUtc as Date)
            .reduce((a, b) => (a > b ? a : b));
    }

    get unitsCombobox() {
        return this.toCombobox(this.units);
    }

    get doseFrequenciesCombobox() {
        const baseItems = this.toCombobox(this.doseFrequencies);
        const itemsWithSeparators: { value: string; label: string; disabled?: boolean }[] = [];

        baseItems.forEach((item) => {
            itemsWithSeparators.push(item);

            if (item.value === '4') {
                itemsWithSeparators.push({
                    value: 'separator-1',
                    label: '──────────',
                    disabled: true,
                });
            }

            if (item.value === '13') {
                itemsWithSeparators.push({
                    value: 'separator-2',
                    label: '──────────',
                    disabled: true,
                });
            }
        });

        return itemsWithSeparators;
    }

    private toCombobox<T extends { id: number; name: string }>(items: T[] = []): { value: string; label: string }[] {
        return items?.map((item) => ({
            value: item.id.toString(),
            label: item.name,
        }));
    }
}

export interface IValidatePrescriptionsRequest {
    patientID: number;
    prescriptionIDs?: number[];
    all?: boolean;
}

export interface IUnlockPrescriptionsRequest {
    patientID: number;
    prescriptionIDs: number[];
    reasonCode: PrescriptionUnlockReason;
    note?: string | null;
}

export interface IPrescriptionActivityEvent {
    id: number;
    action: 'Created' | 'Edited' | 'Deleted' | 'Validated' | 'Unlocked' | string;
    actorHcpID?: number | null;
    actorName?: string | null;
    eventDate: string;
    details?: Record<string, unknown> | null;
}
